import { calendarDateToLocal } from '../../../utils/calendarDate';
import React, { useMemo } from 'react';
import { ClipboardCheck } from 'lucide-react';
import type { TableColumnDef } from '../../../shared/table/types.ts';
import { AppDataTable } from '../../../shared/table/AppDataTable.tsx';
import { useTesterMyControls } from '../../../hooks/analytics/useTesterReports.ts';
import { formatDate, RefreshMeta, StatusChip } from '../controller/shared.tsx';
import type { TesterMyControlsRow } from '../../../request/analytics/testerReports.ts';

/** Days until `due_date` (negative = overdue), or null if there's no due date. */
function daysUntil(dueDate: string | null): number | null {
  if (!dueDate) return null;
  const due = calendarDateToLocal(dueDate);   // the due day as written, not shifted by the viewer's time zone
  if (!due) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);
  return Math.round((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

const DueCountdown: React.FC<{ dueDate: string | null }> = ({ dueDate }) => {
  const days = daysUntil(dueDate);
  if (days == null) {
    return <span className="text-xs text-gray-400">No due date</span>;
  }
  if (days < 0) {
    return (
      <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-semibold text-red-700">
        {Math.abs(days)} day{Math.abs(days) === 1 ? '' : 's'} overdue
      </span>
    );
  }
  if (days === 0) {
    return (
      <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-700">
        Due today
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700">
      Due in {days} day{days === 1 ? '' : 's'}
    </span>
  );
};

/**
 * Report #30 — My Assigned Controls. The dashboard's hero widget: the
 * caller's own test runs (org- and assignee-scoped server-side), soonest
 * due first, with a status chip and a due-date countdown.
 */
export const MyControlsList: React.FC<{ rows?: TesterMyControlsRow[]; assigneeUserId?: number | null }> = ({
  rows: rowsOverride,
  assigneeUserId,
}) => {
  const { data: fetched, isLoading, isError, error, dataUpdatedAt, isFetching, refetch } =
    useTesterMyControls(assigneeUserId);
  const data = rowsOverride ?? fetched;

  const sorted = useMemo(() => {
    const rows = data ?? [];
    return [...rows].sort((a, b) => {
      const aDays = daysUntil(a.due_date);
      const bDays = daysUntil(b.due_date);
      if (aDays == null && bDays == null) return 0;
      if (aDays == null) return 1;
      if (bDays == null) return -1;
      return aDays - bDays;
    });
  }, [data]);

  const columns = useMemo<TableColumnDef<TesterMyControlsRow>[]>(
    () => [
      {
        id: 'control_id',
        columnLabel: 'Control',
        header: () => 'Control',
        cell: ({ row }) => <span className="font-medium text-gray-900">{row.original.control_id}</span>,
        widthPercent: 12,
      },
      {
        id: 'title',
        columnLabel: 'Title',
        header: () => 'Title',
        cell: ({ row }) => row.original.title,
        widthPercent: 24,
      },
      {
        id: 'frequency',
        columnLabel: 'Frequency',
        header: () => 'Frequency',
        cell: ({ row }) => <span className="capitalize text-gray-600">{row.original.frequency ?? '—'}</span>,
        widthPercent: 12,
      },
      {
        id: 'workflow_status',
        columnLabel: 'Status',
        header: () => 'Status',
        cell: ({ row }) => <StatusChip value={row.original.workflow_status} />,
        widthPercent: 14,
      },
      {
        id: 'due_date',
        columnLabel: 'Due',
        header: () => 'Due',
        cell: ({ row }) => <DueCountdown dueDate={row.original.due_date} />,
        widthPercent: 16,
      },
      {
        id: 'period',
        columnLabel: 'Testing Period',
        header: () => 'Testing Period',
        cell: ({ row }) =>
          `${formatDate(row.original.testing_period_start)} – ${formatDate(row.original.testing_period_end)}`,
        widthPercent: 22,
      },
    ],
    []
  );

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-blue-100 text-blue-600">
            <ClipboardCheck className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-gray-900">My Assigned Controls</h2>
            <p className="text-xs text-gray-500">Your current test runs, soonest due first</p>
          </div>
        </div>
        <RefreshMeta updatedAt={dataUpdatedAt} onRefresh={refetch} isFetching={isFetching} />
      </div>
      <AppDataTable<TesterMyControlsRow>
        data={sorted}
        columns={columns}
        loading={isLoading}
        error={isError ? error?.message ?? 'Failed to load your assigned controls.' : null}
        emptyMessage="No controls are currently assigned to you."
        searchable
        searchPlaceholder="Search my controls..."
        paginated
        defaultPageSize={10}
        showPageSizeSelector
      />
    </div>
  );
};

export default MyControlsList;
